import type { SfContext } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { subDays } from "date-fns";
import React from "react";

import type { AssociatedPitchedContent } from "../../../../store/editContent";
import type { SfObject } from "../../../../store/insightLinkage";
import { SfObjectType } from "../../../../store/insightLinkage";
import { ContentAction } from "../../../../types/content";
import { formatDate } from "../../../../utils/date.utils";
import { getAdvertiserId } from "../../../../utils/permission.utils";
import {
    DAYS_INTERVAL_FOR_SF_ACTIVITY_QUERY,
    NO_OPPORTUNITY_DROPDOWN_ITEM,
    OPPORTUNITY_NOT_CREATED_DROPDOWN_LABEL,
} from "../../../../utils/salesforce.utils";
import ActivityDropdown from "../../../common/ActivityDropdown";
import { DisabledDropdown } from "../../../common/DisabledDropdown";
import OpportunityDropdown from "../../../common/OpportunityDropdown";

interface UploadPitchedContentSalesforceSelectorProps {
    editContent: AssociatedPitchedContent | undefined;
    sfAccountId: string | undefined;
    sfObject: SfObject;
    setSfObject: (sfObject: SfObject) => void;
    contentSfContext: SfContext | undefined;
    closeModal?: () => void;
}

export const OPPORTUNITY_DROPDOWN_DISABLED_ACCOUNT_MISMATCH =
    "Associated opportunity can only be changed when signed in with the currently associated account.";
export const OPPORTUNITY_DROPDOWN_DISABLED_ACTIVITY_ASSOCIATED =
    "Associated opportunity cannot be changed after associating to an activity.";
export const ACTIVITY_DROPDOWN_DISABLED_ACCOUNT_MISMATCH =
    "Associated activity can only be changed when signed in with the currently associated account.";

export const UploadPitchedContentSalesforceSelector: React.FunctionComponent<
    UploadPitchedContentSalesforceSelectorProps
> = (props: UploadPitchedContentSalesforceSelectorProps) => {
    const {
        editContent,
        sfAccountId,
        sfObject,
        setSfObject,
        contentSfContext,
        closeModal,
    } = props;

    const isMatchingSalesforceAccount = () => {
        return sfAccountId === getAdvertiserId();
    };

    const hasActivityAssociation = () =>
        editContent?.associations?.some(
            (association) =>
                association.salesforceAssociation?.salesforceObjectType ===
                SfObjectType.ACTIVITY,
        ) ?? false;

    const renderOpportunityDropdown = (): React.ReactElement => {
        if (!isMatchingSalesforceAccount()) {
            return (
                <DisabledDropdown
                    label="Salesforce Opportunity"
                    displayValue={
                        editContent?.accountOpportunities?.[0]
                            ?.opportunityName ?? "-"
                    }
                    tooltip={OPPORTUNITY_DROPDOWN_DISABLED_ACCOUNT_MISMATCH}
                />
            );
        } else if (hasActivityAssociation()) {
            return (
                <DisabledDropdown
                    label="Salesforce Opportunity"
                    displayValue={OPPORTUNITY_NOT_CREATED_DROPDOWN_LABEL}
                    tooltip={OPPORTUNITY_DROPDOWN_DISABLED_ACTIVITY_ASSOCIATED}
                />
            );
        } else {
            return (
                <OpportunityDropdown
                    sfObject={sfObject}
                    setSfObject={setSfObject}
                    defaultOpportunity={NO_OPPORTUNITY_DROPDOWN_ITEM}
                    includeClosed
                    closeModal={closeModal}
                />
            );
        }
    };

    const renderActivityDropdown = (): React.ReactElement => {
        if (sfObject.sfObjectType !== SfObjectType.ACTIVITY) {
            return <></>;
        } else if (!isMatchingSalesforceAccount()) {
            return (
                <DisabledDropdown
                    label="Salesforce Activity"
                    displayValue={
                        editContent?.associations?.[0]
                            ?.salesforceActivityMetadata?.activityName ?? "-"
                    }
                    tooltip={ACTIVITY_DROPDOWN_DISABLED_ACCOUNT_MISMATCH}
                />
            );
        } else {
            return (
                <ActivityDropdown
                    sfObject={sfObject}
                    setSfObject={setSfObject}
                    startDate={formatDate(
                        subDays(
                            new Date(),
                            DAYS_INTERVAL_FOR_SF_ACTIVITY_QUERY,
                        ),
                    )}
                    endDate={formatDate(new Date())}
                    contentAction={ContentAction.UPLOAD}
                    closeModal={closeModal}
                />
            );
        }
    };

    return (
        <>
            <DisabledDropdown
                label={"Salesforce Account"}
                displayValue={contentSfContext?.name ?? "Account not found"}
                tooltip={
                    editContent
                        ? "Associated account cannot be edited after upload"
                        : undefined
                }
            />
            {renderOpportunityDropdown()}
            {renderActivityDropdown()}
        </>
    );
};
